import type { Clip, Range } from '../types'
import { clipKept, totalDuration } from './ranges'

// GĐ7 — phân tích cấp câu chuyện (heuristic, chạy offline).

export interface Beat {
  label: string
  start: number
  end: number
  color: string
}

/** Chia transcript thành 4 beat theo tỉ lệ: Hook → Dẫn dắt → Cao trào → CTA. */
export function computeBeats(clip: Clip): Beat[] {
  const kept = clip.words.filter((w) => !w.removed)
  if (kept.length < 4) return []
  const t0 = kept[0].start
  const t1 = kept[kept.length - 1].end
  const span = t1 - t0
  const marks = [0, 0.15, 0.6, 0.85, 1].map((f) => t0 + f * span)
  const defs: [string, string][] = [
    ['Hook', '#ff5d6c'],
    ['Dẫn dắt', '#6d7bff'],
    ['Cao trào', '#ffb23e'],
    ['CTA', '#00d4a0'],
  ]
  return defs.map(([label, color], i) => ({ label, color, start: marks[i], end: marks[i + 1] }))
}

/** Hook Doctor: chấm điểm 3 giây đầu + gợi ý. */
export function hookScore(clip: Clip): { score: number; tips: string[] } {
  const kept = clip.words.filter((w) => !w.removed)
  if (!kept.length) return { score: 0, tips: ['Chưa có transcript — hãy phiên âm trước.'] }
  const t0 = kept[0].start
  const first3 = kept.filter((w) => w.start < t0 + 3)
  const text = first3.map((w) => w.text).join(' ').toLowerCase()
  let score = 40
  const tips: string[] = []
  if (first3.length >= 8) score += 25
  else tips.push('Nói nhiều thông tin hơn trong 3 giây đầu — hook đang thưa.')
  if (/\?|sao|tại sao|làm sao|bạn có|bạn đã|how|why|what/.test(text)) score += 20
  else tips.push('Mở đầu bằng một câu hỏi hoặc điều gây tò mò.')
  if (/bí mật|miễn phí|ngay|đừng|dừng|bỏ lỡ|secret|free|stop|never/.test(text)) score += 15
  else tips.push('Thêm từ khóa "hook": bí mật, miễn phí, đừng bỏ lỡ…')
  return { score: Math.min(100, score), tips: tips.slice(0, 2) }
}

/** Đường cong giữ chân (heuristic): mật độ lời nói cao → giữ chân tốt hơn. */
export function retentionCurve(clip: Clip, n = 24): number[] {
  const kept = clip.words.filter((w) => !w.removed)
  const editedLen = totalDuration(clipKept(clip))
  if (kept.length < 3 || editedLen <= 0) return []
  const t0 = kept[0].start
  const t1 = kept[kept.length - 1].end
  const span = t1 - t0 || 1
  const points: number[] = []
  for (let i = 0; i < n; i++) {
    const a = t0 + (span * i) / n
    const b = t0 + (span * (i + 1)) / n
    const density = kept.filter((w) => w.start >= a && w.start < b).length
    const base = 100 - (i / n) * 35 // suy giảm tự nhiên theo thời lượng
    const boost = Math.min(15, density * 3)
    const penalty = density === 0 ? 20 : 0 // đoạn im lặng tụt mạnh
    points.push(Math.max(20, Math.min(100, base + boost - penalty)))
  }
  return points
}

export interface Highlight extends Range {
  score: number
  preview: string
}

/** Tự đề xuất các đoạn "đáng cắt short" theo mật độ lời nói. */
export function autoHighlights(clip: Clip, win = 30, k = 3): Highlight[] {
  const kept = clip.words.filter((w) => !w.removed)
  if (kept.length < 5) return []
  const end = kept[kept.length - 1].end
  const cands: Highlight[] = []
  for (let i = 0; i < kept.length; i += 4) {
    const s = kept[i].start
    const e = Math.min(s + win, end)
    const inWin = kept.filter((w) => w.start >= s && w.start < e)
    if (inWin.length < 3) continue
    cands.push({
      start: s,
      end: e,
      score: inWin.length,
      preview: inWin.slice(0, 10).map((w) => w.text).join(' '),
    })
  }
  cands.sort((a, b) => b.score - a.score)
  const picked: Highlight[] = []
  for (const c of cands) {
    if (picked.every((p) => c.start > p.end || c.end < p.start)) {
      picked.push(c)
      if (picked.length >= k) break
    }
  }
  return picked.sort((a, b) => a.start - b.start)
}
